/**
 * Contexts a relation can belong to, used to draw one kind of story at a time on a canvas that shows
 * everything at once otherwise.
 *
 * The grouping lives here rather than on the API because `RelationType` is a fixed server-side enum:
 * authors pick from a closed vocabulary, so nothing needs to travel with the data. If user-defined
 * relation types ever land, this map becomes the seed for the field that replaces it.
 */
export type RelationGroupId =
	| "all"
	| "family"
	| "social"
	| "allegiance"
	| "places"
	| "possessions"
	| "companions"
	| "events"
	| "lineage"
	| "lore"
	| "other";

/** Selector order: "all" first, then roughly most to least used. */
export const RELATION_GROUP_IDS: RelationGroupId[] = [
	"all",
	"family",
	"social",
	"allegiance",
	"places",
	"possessions",
	"companions",
	"events",
	"lineage",
	"lore",
	"other"
];

/**
 * Every value of the backend `RelationType` enum, mapped to its context. Both halves of a pair always
 * share a group — a relation is one fact, and hiding one direction of it while drawing the other
 * would be incoherent.
 *
 * Anything absent falls to "other" rather than disappearing, so a relation type added to the API but
 * not yet classified here still draws under "All" and under "Other".
 */
const GROUP_BY_RELATION_TYPE: Record<string, RelationGroupId> = {
	// Family — blood and marriage.
	Father: "family",
	Mother: "family",
	Husband: "family",
	Wife: "family",
	Son: "family",
	Daughter: "family",
	Brother: "family",
	Sister: "family",
	Grandfather: "family",
	Grandmother: "family",
	Grandson: "family",
	Granddaughter: "family",
	Uncle: "family",
	Aunt: "family",
	Nephew: "family",
	Niece: "family",
	Cousin: "family",

	// Social — personal bonds outside the family.
	Friend: "social",
	Acquaintance: "social",
	LoveInterest: "social",
	Rival: "social",
	Enemy: "social",

	// Allegiance — who someone stands with, serves, leads or guards. The "workplace" context of a
	// world whose organizations are houses, orders and guilds more often than employers.
	MemberOf: "allegiance",
	HasMember: "allegiance",
	LedBy: "allegiance",
	LeaderOf: "allegiance",
	BelongsToPack: "allegiance",
	HasPackMember: "allegiance",
	AlliedWith: "allegiance",
	Opposes: "allegiance",
	OpposedBy: "allegiance",
	Protects: "allegiance",
	ProtectedBy: "allegiance",

	// Places — where things are, live, or are kept.
	LocatedIn: "places",
	Contains: "places",
	HomeOf: "places",
	ResidesIn: "places",
	SeatOf: "places",
	HasSeat: "places",
	NeighboringRegion: "places",
	PartOfWorld: "places",
	WorldOf: "places",
	Inhabits: "places",
	HabitatOf: "places",
	NativeTo: "places",
	NativeLandOf: "places",
	SeenIn: "places",
	SightingOf: "places",
	StoredAt: "places",
	Stores: "places",

	// Possessions — objects and who owns, made, gave or wields them.
	Owner: "possessions",
	BelongsTo: "possessions",
	PrimaryWeapon: "possessions",
	Wielder: "possessions",
	ForgedBy: "possessions",
	ForgerOf: "possessions",
	GiftedTo: "possessions",
	RecipientOf: "possessions",
	InheritedBy: "possessions",
	InheritorOf: "possessions",
	UsedBy: "possessions",
	UserOf: "possessions",

	// Companions — creatures bound to a person.
	BondedTo: "companions",
	BondedWith: "companions",
	CompanionOf: "companions",
	HasCompanion: "companions",

	// Events — what happened, where, to whom, and what it caused.
	OccurredAt: "events",
	SiteOf: "events",
	Involved: "events",
	InvolvedIn: "events",
	TriggeredBy: "events",
	Triggered: "events",
	ResultedIn: "events",
	ResultOf: "events",
	PartOfEra: "events",
	EraIncludes: "events",
	WitnessedBy: "events",
	Witnessed: "events",

	// Lineage — succession and descent that is not family: titles passed on, species descending,
	// things tracing back to an origin.
	PredecessorOf: "lineage",
	SuccessorOf: "lineage",
	AncestorOf: "lineage",
	DescendantOf: "lineage",
	OriginatesFrom: "lineage",
	OriginOf: "lineage",

	// Lore — belief, meaning and influence.
	PracticedBy: "lore",
	Practices: "lore",
	SymbolizedBy: "lore",
	SymbolOf: "lore",
	Influences: "lore",
	InfluencedBy: "lore",

	// Other — deliberately unclassified: generic links with no context of their own.
	AssociatedWith: "other",
	PartOf: "other",
	HasPart: "other",
	Other: "other"
};

/** The context a relation type belongs to; "other" for anything not classified above. */
export const relationGroupOf = (relationType: string): RelationGroupId =>
	GROUP_BY_RELATION_TYPE[relationType] ?? "other";

/** True when a relation should be drawn for the selected group. "all" draws everything. */
export const isInRelationGroup = (relationType: string, group: RelationGroupId): boolean =>
	group === "all" || relationGroupOf(relationType) === group;

/** Narrows a stored or user-supplied string back to a known group, falling back to "all". */
export const toRelationGroupId = (value: string | null): RelationGroupId =>
	RELATION_GROUP_IDS.includes(value as RelationGroupId) ? (value as RelationGroupId) : "all";
